import dayjs from 'dayjs';
import { OptionsObject } from 'notistack';
import { Interval } from '../../../../types';
import { PhenologicalStateWithId } from '../../../../requests/api/apiTypes';
import { CriticalPoint } from './types';

/**
 * Validates critical points values are consistent.
 * @param {CriticalPoint[]} criticalPoints - Points to validate.
 * @param {(message: string, options?: OptionsObject) => void} enqueueSnackbar - Snackbar callback.
 * @param {number} [minValue] - Minimum allowed value.
 * @param {number} [maxValue] - Maximum allowed value.
 * @returns {boolean} Whether the points are valid.
 */
const validatePoints = (
  criticalPoints: CriticalPoint[],
  enqueueSnackbar: (message: string, options?: OptionsObject) => void,
  minValue?: number,
  maxValue?: number,
): boolean => {
  if (minValue === undefined || maxValue === undefined) {
    enqueueSnackbar('Error: No estan definidos los puntos mínimo y máximo', { variant: 'error' });
    return false;
  }
  // Only validate that point 4 is between min and max
  const yellowPoint = criticalPoints.find((p) => p.id === 4);

  if (yellowPoint && yellowPoint.value) {
    if (minValue !== 0 && yellowPoint.value <= minValue) {
      enqueueSnackbar(`Error: ${yellowPoint.customLabel} (${yellowPoint.value}) debe ser mayor que Mínimo (${minValue})`, { variant: 'error' });
      return false;
    }
    if (maxValue !== 0 && yellowPoint.value >= maxValue) {
      enqueueSnackbar(`Error: ${yellowPoint.customLabel} (${yellowPoint.value}) debe ser menor que Máximo (${maxValue})`, { variant: 'error' });
      return false;
    }
  }

  return true;
};

/**
 * Finds the first phenological state overlapping the provided interval.
 * @param {PhenologicalStateWithId[]} phenologicalStates - States to search.
 * @param {Interval} interval - Interval with start and end.
 * @returns {PhenologicalStateWithId | undefined} First overlapping state, if any.
 */
const getPhenologicalStateFromInterval = (
  phenologicalStates: PhenologicalStateWithId[],
  interval: Interval,
) => {
  const intervalStart = dayjs(interval.startDate);
  const intervalEnd = dayjs(interval.endDate);
  return phenologicalStates.find((state) => {
    const sStart = dayjs(state.startDate);
    const sEnd = dayjs(state.endDate);
    return !(sEnd.isBefore(intervalStart, 'day') || sStart.isAfter(intervalEnd, 'day'));
  });
};

export { validatePoints, getPhenologicalStateFromInterval };
